import { Inject, Injectable } from '@angular/core';
import { IAuthService } from './auth.type';
import { StorageService } from '../local-storage/storage.service';
import { STORAGE_TOKEN, StorageKey } from '../local-storage/storage.type';
import { Amplify, Auth } from 'aws-amplify';
import { AuthConfig } from './auth.config';
import { APIAuthFlowService } from '../API/APIAuthFlow.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { OAuthToken } from '../../models/API.models';
import { FederatedUser } from '@aws-amplify/auth/src/types/Auth';
import { ICredentials } from 'aws-amplify/lib/Common/types/types';
import { AuthStatus } from '../user/user.type';
import { TokenService } from './token.service';
import { NGXLogger } from 'ngx-logger';
import { Location } from '@angular/common';
import { RoutingUtils } from '../../../routing/routing.utils';
import { DialogService } from '../dialog/dialog.service';
import { MatDialogConfig } from '@angular/material/dialog';
import { PopupContentType, PopupCustomProps } from '../../../shared/connection-popup/connection-popup.types';
@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService {
  private storageKey = StorageKey.AUTH;
  private localStorageService: StorageService;

  public sessionUpdate$: BehaviorSubject<undefined> =
    new BehaviorSubject<undefined>(undefined);

  public isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(STORAGE_TOKEN) localStorageService: StorageService,
    private authConfig: AuthConfig,
    private apiAuthFlowService: APIAuthFlowService,
    private tokenService: TokenService,
    private logger: NGXLogger,
    private location: Location,
    private routingUtils: RoutingUtils,
    private dialogService: DialogService
  ) {
    this.localStorageService = localStorageService;

    // AWS Amplify specific refresh flow
    this.logger.info('[AuthService] Attach - refreshHandler.');

    const config = this.authConfig.awsAmplify;
    this.logger.debug(`[AuthService] Calling Amplify.configure()`);
    Amplify.configure({
      ...config,
      Auth: {
        ...config.Auth,
        refreshHandlers: {
          [this.authConfig.federatedSignIn.provider]: this.refreshHandler,
        },
      },
    });
  }

  refreshHandler = () => {
    return (async () => {
      const session = this.getSession();
      this.logger.info('[AuthService] Amplify refreshHandler starting');
      try {
        const newSession = await this.OAuth2_ExchangeRefreshTokenForToken(
          session.refresh_token
        );

        this.updateSession(newSession);
        this.logger.debug('[AuthService] REFRESHED session: ', newSession);

        return {
          token: newSession.id_token,
          expires_at: newSession.expires_in,
        };
      } catch (err) {
        this.logger.error('[AuthService] Refresh Token Error:', err);
        return err;
      }
    })();
  }

  // Amplify auth
  async authenticate(code: string): Promise<AuthStatus> {
    this.logger.debug('Code: ', code);

    try {
      await this.signOut();
      const oAuthToken: OAuthToken = await this.OAuth2_ExchangeAuthCodeForToken(
        code
      );
      this.logger.debug('[AuthService] authenticate -  session: ', oAuthToken);
      this.logger.info(
        '[AuthService] authenticate - expires at: ',
        oAuthToken.expires_in
      );

      const user = await this.federatedSignIn(oAuthToken);

      this.logger.debug('[AuthService] authenticate -  User: ', user);

      return Promise.resolve(AuthStatus.SUCCESS);
    } catch (err) {
      this.logger.error('[AuthService] authenticate - error:', err);
      return Promise.resolve(AuthStatus.ERROR);
    }
  }

  async forceRefresh(): Promise<AuthStatus> {
    const session = this.getSession();
    this.logger.info('[AuthService] App refreshHandler starting');

    try {
      const newSession = await this.OAuth2_ExchangeRefreshTokenForToken(
        session.refresh_token
      );
      this.logger.debug('[AuthService] REFRESHED session: ', newSession);

      // cleaning out aws-amplify-federatedInfo from LocalStorage
      // to avoid warning of undifend behaviour in Amplify Auth lib
      this.tokenService.removeStoredAuthenticatedUser();
      await this.federatedSignIn(newSession);
      return AuthStatus.SUCCESS;
    } catch (err) {
      this.logger.error('[AuthService] Refresh Token Error:', err);
      return AuthStatus.ERROR;
    }
  }

  /* istanbul ignore next */
  async testForcedFederatedSignIn(): Promise<void> {
    const session = this.getSession();
    this.tokenService.removeStoredAuthenticatedUser();
    const user = await this.federatedSignIn(session);
    this.logger.debug('[AuthService] testForcedFederatedSignIn -  User: ', user);
  }

  async OAuth2_ExchangeAuthCodeForToken(code: string): Promise<OAuthToken> {
    this.logger.debug(
      '[AuthService] Exchanging an authorization code for tokens'
    );

    const body = {
      grant_type: this.authConfig.oAuthConfig.grantType,
      client_id: this.authConfig.awsAmplify.Auth.userPoolWebClientId,
      code,
      redirect_uri: this.authConfig.oAuthConfig.redirectURI,
    };

    const resp = await firstValueFrom(
      this.apiAuthFlowService.authenticate(body)
    );
    this.logger.debug('[AuthService] /oauth2/token response: ', resp);

    return resp;
  }

  async OAuth2_ExchangeRefreshTokenForToken(
    refreshToken: string
  ): Promise<OAuthToken> {
    this.logger.info(
      '[AuthService] Exchanging a refresh token for renew the access token'
    );

    const body = {
      grant_type: 'refresh_token',
      client_id: this.authConfig.awsAmplify.Auth.userPoolWebClientId,
      refresh_token: refreshToken,
    };

    const resp = await firstValueFrom(
      this.apiAuthFlowService.authenticate(body)
    );
    this.logger.debug('[AuthService] refreshed /oauth2/token response: ', resp);

    return resp;
  }

  async federatedSignIn(oAuthToken: OAuthToken): Promise<FederatedUser> {
    let user: FederatedUser = {
      name: '',
    };
    const { expiresIn, provider } = this.authConfig.federatedSignIn;

    this.logger
      .debug(`[AuthService] starting federatedSignIn\nprovider: ${provider} token: ${oAuthToken.id_token}\n
      expiresIn: ${expiresIn}`);

    const credentials: ICredentials = await Auth.federatedSignIn(
      provider,
      {
        token: oAuthToken.id_token,
        // TODO: Set real value, instead of *1000
        expires_at: expiresIn * 1000 + new Date().getTime(),
      },
      user
    );
    this.logger.debug('[Amplify] credentials: ', credentials);

    user = await this.tokenService.currentAuthenticatedUser();

    this.updateSession(oAuthToken);
    this.location.go('');
    this.logger.debug('[Amplify] Clear code param - done.');

    return user;
  }

  getSession(): OAuthToken {
    return this.localStorageService.getItem<OAuthToken>(this.storageKey);
  }

  setSession(key: StorageKey, value: OAuthToken): void {
    this.localStorageService.setItem(key, value);

    this.sessionUpdate$.next(undefined);
  }

  removeSession(): void {
    this.localStorageService.removeItem(this.storageKey);
  }

  updateSession(oAuthToken: OAuthToken): void {
    const newSession = { ...oAuthToken };
    const oldSession = this.getSession();

    // After a call to OAuth2_ExchangeRefreshTokenForToken() refresh_token is not transmitted from BE.
    // refresh_token is transmitted only upon initial login
    // the following code preserves refresh_token between token refreshes
    if (oldSession?.refresh_token && !newSession.refresh_token) {
      this.logger.debug(
        'OldSession have refresh_token while newSession does not have'
      );
      newSession.refresh_token = oldSession.refresh_token;
    }

    this.logger.debug('[AuthService] Storing session: ', newSession);

    // Amplify Cache is used just for test purposes -
    // we shall introduce our own storage which will allow to easily swap
    // localStorage to sessionStorage if needed
    this.setSession(StorageKey.AUTH, newSession);
  }

  async isAuthenticated(): Promise<boolean> {
    let isAuthenticatedStatus = false;
    try {
      const user = await this.tokenService.currentAuthenticatedUser();
      this.logger.debug(`[AuthService] isAuthenticated:`, user);

      isAuthenticatedStatus = true;
    } catch (error) {
      this.logger.error(`[AuthService] isAuthenticated error: ${error}`);
    }

    this.isAuthenticated$.next(isAuthenticatedStatus);

    return isAuthenticatedStatus;
  }

  /**
   * Logout Out methods
   */

  async signOut(): Promise<any> {
    this.logger.info('Sign Out current user and clear session.');

    this.removeSession();
    // TODO: remove await so Auth.signOut is not awaited
    return await Auth.signOut({ global: true });
  }

  async revokeToken(): Promise<boolean> {
    this.logger.debug('[AuthService] revokeToken');
    const session = this.getSession();

    const body = {
      token: session.refresh_token,
      client_id: this.authConfig.awsAmplify.Auth.userPoolWebClientId,
    };

    try {
      const resp = await firstValueFrom(
        this.apiAuthFlowService.revoke(body)
      );
      this.logger.debug('[AuthService] /oauth2/revoke response: ', resp);
      return true;
    }
    catch (err) {
      this.logger.error('[AuthService] /oauth2/revoke Error: ', err);

      throw err;
    }
  }

  async retryWithBackoff(revokeTokenCB: () => Promise<boolean>, maxAttempts: number, delay: number): Promise<void> {
    let retryIndex = 1;
    let revokeTokenError;
    let revokeTokenStatus;

    while (true) {
      try {
        revokeTokenStatus = await revokeTokenCB();
      }
      catch (error) {
        revokeTokenStatus = false;
        revokeTokenError = error;
      }
      if (revokeTokenStatus) {
        return;
      }

      if (retryIndex >= maxAttempts) {
        throw revokeTokenError;
      }

      const nextDelay = (2 ** retryIndex) * delay;
      this.logger.log(`[AuthService] - logout: revokeToken failed. Retry ${retryIndex + 1} in ${nextDelay}ms.`);
      await new Promise(resolve => setTimeout(resolve, nextDelay));
      retryIndex++;
    }
  }

  async logout(shouldRedirect: boolean = true): Promise<boolean> {
    this.logger.log('[AuthService] - logout: Logout starts.');

    this.logger.log('[AuthService] - logout: revokeToken with exponential backoff retry logic.');
    try {
      await this.retryWithBackoff(this.revokeToken.bind(this), 3, 1000);
      this.logger.log('[AuthService] - logout: revokeToken success.');
    } catch (error) {
      this.logger.error('[AuthService] -  logout: revokeToken fails.');

      this.dialogService.open({
        width: '450px'
      }, {
        content: error.name === 'HttpErrorResponse' 
        ? PopupContentType.INTERNET_CONNECTION_ISSUES 
        : PopupContentType.SMTH_WENT_WRONG,
      });

      return false;
    }

    try {
      await this.signOut();
    } catch (err) {
      this.logger.debug('[AuthService] - logout: signOut failed. What shall we do?');
    }

    try {
      this.tokenService.removeStoredAuthenticatedUser();
    } catch (err) {
      this.logger.debug('[AuthService] - logout: tokenService.removeStoredAuthenticatedUser failed. What shall we do?');
    }

    this.logger.debug(`[AuthService] Logout finishing.`);

    if (shouldRedirect) {
      const logoutUrl = this.logoutUrl();
      this.logger.debug(`[AuthService] - logout: Redirecting logout to: ${logoutUrl}`);
      this.routingUtils.routeToExternalUrl(logoutUrl);
    } else {
      this.logger.debug('[AuthService] - logout: skipping redirect');
    }

    return true;
  }

  public logoutUrl(): string {
    const url = `${this.authConfig.oAuthConfig.domain}/logout?client_id=${this.authConfig.awsAmplify.Auth.userPoolWebClientId}&logout_uri=${this.authConfig.oAuthConfig.logoutURI}`;
    this.logger.debug('[AuthConfig] logoutUrl', url);
    return url;
  }

  ssoUrl(): string {
    const { domain, redirectURI, identityProvider } = this.authConfig.oAuthConfig;
    const clientId = this.authConfig.awsAmplify.Auth.userPoolWebClientId;
    return `${domain}/oauth2/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectURI}&identity_provider=${identityProvider}`;
  }
}
